import JSZip from 'jszip';
import {
  ActivationPackWithTarget,
  AssetConfig,
  ASSET_TYPE_INFO,
  DEFAULT_PROMO_TONES,
  GeneratedAsset,
  GeneratedAssetType,
  PromoTemplateType,
  PromoTone,
  PROMO_TEMPLATE_OPTIONS,
  TouchpointConfig,
} from '@/types/activation/activation-pack';
import { ActivationPackService } from '@/services/database/ActivationPackService';
import {
  buildPackDocument,
  PACK_DOCUMENTS,
  resolvePackPrintKit,
} from '@/services/activation/print/packDocuments';

export interface ZipBundleOptions {
  /** The pack used to build guides, print instructions, and promo-copy files. */
  pack: ActivationPackWithTarget;
  /** Immersive presentation microsite URL (`/activate/:packId`). */
  presentationUrl?: string;
  /** Device-detecting smart link (`/get/:packId`). */
  smartLinkUrl?: string;
  /** Override only when a caller intentionally exports a smaller manifest. */
  expectedAssetTypes?: GeneratedAssetType[];
}

export const ACTIVATION_PACK_STARTER_QUANTITIES = {
  stickers: 100,
  posters: 20,
  appPosters: 10,
} as const;

/**
 * The assets the bundle always carries.
 *
 * `poster_pack_pdf` and `lookbook_pdf` used to be on this list and are not any
 * more. Both are rasterised in the browser (html2canvas over twenty-odd A4
 * pages at print density), which is slow and fails outright on larger packs —
 * and because the bundle is all-or-nothing, either failure took the whole
 * download with it. The printed poster and placement material now ships as the
 * three booklets below, built as vector documents that cannot fail that way.
 * Both types still exist and are still downloadable on their own.
 */
const ALWAYS_EXPECTED_ASSET_TYPES: GeneratedAssetType[] = [
  'qr_png',
  'qr_svg',
  'embed_code',
  'app_poster_png',
  'app_poster_pdf',
];

/** The canonical contract behind both the prominent download and its manifest. */
export function getExpectedActivationAssetTypes(
  touchpoints: TouchpointConfig,
  assetConfig?: Pick<AssetConfig, 'product_slug'> | null,
): GeneratedAssetType[] {
  const expected = [...ALWAYS_EXPECTED_ASSET_TYPES];
  // A pack pointed at a catalogue product also carries that product's manual QR.
  if (assetConfig?.product_slug) expected.push('product_qr_png', 'product_qr_svg');
  if (touchpoints.qr_sticker) expected.push('sticker_pdf');
  if (touchpoints.email_signature) expected.push('email_signature_html');
  if (touchpoints.social_card) expected.push('social_card_png');
  return expected;
}

const hasUsableData = (asset: GeneratedAsset): boolean => {
  if (asset.data instanceof Blob) return asset.data.size > 0;
  return typeof asset.data === 'string' && asset.data.trim().length > 0;
};

export interface ActivationAssetCompleteness {
  expected: GeneratedAssetType[];
  included: GeneratedAssetType[];
  missing: GeneratedAssetType[];
  complete: boolean;
}

/** Pure completeness inspection, exported so the download contract is testable. */
export function inspectActivationAssetCompleteness(
  assets: GeneratedAsset[],
  expected: GeneratedAssetType[],
): ActivationAssetCompleteness {
  const includedSet = new Set(
    assets.filter(hasUsableData).map((asset) => asset.type),
  );
  const included = expected.filter((type) => includedSet.has(type));
  const missing = expected.filter((type) => !includedSet.has(type));
  return { expected, included, missing, complete: missing.length === 0 };
}

export function assertCompleteActivationAssets(
  assets: GeneratedAsset[],
  expected: GeneratedAssetType[],
): void {
  const result = inspectActivationAssetCompleteness(assets, expected);
  if (!result.complete) {
    const labels = result.missing.map((type) => ASSET_TYPE_INFO[type].label);
    throw new Error(`Activation pack is incomplete. Missing: ${labels.join(', ')}`);
  }
}

export interface ActivationPromoCompleteness {
  expected: PromoTemplateType[];
  included: PromoTemplateType[];
  missing: PromoTemplateType[];
  complete: boolean;
}

export interface DefaultPromoVariantRequirement {
  template: PromoTemplateType;
  tone: PromoTone;
  label: string;
}

export function getMissingDefaultPromoVariants(
  pack: ActivationPackWithTarget,
): DefaultPromoVariantRequirement[] {
  return PROMO_TEMPLATE_OPTIONS.flatMap((option) => {
    const tone = DEFAULT_PROMO_TONES[option.value];
    const body = pack.promo_content?.[option.value]?.[tone]?.body;
    return body?.trim()
      ? []
      : [{ template: option.value, tone, label: option.label }];
  });
}

/** Inspect the canonical default-tone promo set included in a full bundle. */
export function inspectDefaultPromoCompleteness(
  pack: ActivationPackWithTarget,
): ActivationPromoCompleteness {
  const expected = PROMO_TEMPLATE_OPTIONS.map((option) => option.value);
  const missing = getMissingDefaultPromoVariants(pack).map(({ template }) => template);
  const missingSet = new Set(missing);
  const included = expected.filter((type) => !missingSet.has(type));
  return { expected, included, missing, complete: missing.length === 0 };
}

export function assertCompleteDefaultPromoTemplates(
  pack: ActivationPackWithTarget,
): void {
  const result = inspectDefaultPromoCompleteness(pack);
  if (!result.complete) {
    const labels = result.missing.map(
      (type) => PROMO_TEMPLATE_OPTIONS.find((option) => option.value === type)?.label || type,
    );
    throw new Error(`Activation pack is incomplete. Missing promo copy: ${labels.join(', ')}`);
  }
}

async function assetToBlob(asset: GeneratedAsset): Promise<Blob> {
  let blob: Blob;
  if (asset.data instanceof Blob) {
    blob = asset.data;
  } else if (typeof asset.data === 'string') {
    if (asset.data.startsWith('data:')) {
      const res = await fetch(asset.data);
      if (!res.ok) throw new Error(`Failed to read ${asset.filename}`);
      blob = await res.blob();
    } else if (/^https?:\/\//i.test(asset.data)) {
      const res = await fetch(asset.data);
      if (!res.ok) throw new Error(`Failed to fetch ${asset.filename}`);
      blob = await res.blob();
    } else {
      blob = new Blob([asset.data], { type: asset.mimeType });
    }
  } else {
    throw new Error(`Unsupported asset data type for ${asset.type}`);
  }

  if (blob.size === 0) throw new Error(`${asset.filename} is empty`);
  return blob;
}

export interface BundledBooklet {
  filename: string;
  html: string;
  pages: number;
}

/**
 * The three booklets as standalone A4 documents. Opened in any browser they
 * print — or save as PDF — at exactly the size they were laid out at, with
 * every QR code still vector.
 *
 * Returns an empty list for a Genie with no published page: every code in a
 * booklet points at that page, and a printed run pointing at the wrong place
 * cannot be recalled. A booklet that fails to build is skipped with a console
 * error rather than taking the whole bundle down with it — the rest of the
 * pack is still worth having.
 */
export async function buildPackBooklets(
  pack: ActivationPackWithTarget,
): Promise<BundledBooklet[]> {
  const kit = resolvePackPrintKit(pack);
  if (!kit) return [];

  const booklets: BundledBooklet[] = [];
  for (const meta of PACK_DOCUMENTS) {
    try {
      const built = await buildPackDocument(meta.id, kit);
      booklets.push({
        filename: `${kit.slug}-${meta.id}.html`,
        html: built.html,
        pages: built.pages,
      });
    } catch (error) {
      console.error(`Failed to bundle the ${meta.title.toLowerCase()}:`, error);
    }
  }
  return booklets;
}

function sanitizeFolder(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'activation-pack';
}

/** Root-level getting-started guide for the customer, not the print vendor. */
export function buildReadmeText(options: ZipBundleOptions): string {
  const { pack, presentationUrl, smartLinkUrl } = options;
  const name = ActivationPackService.getTargetName(pack);
  const targetAction = ActivationPackService.getTargetType(pack) === 'portal' ? 'Open' : 'Talk to';
  const targetUrl = ActivationPackService.getTargetUrl(pack);
  const phoneUrl = ActivationPackService.getPhoneUrl(pack);

  const lines: string[] = [
    `${name} — Activation Pack`,
    '='.repeat(48),
    '',
    'Everything in this folder is ready for a specific job:',
    '',
    '  printer-ready/         The three booklets, plus sticker and app poster artwork.',
    '  qr-codes/              PNG and SVG QR codes for new materials.',
    '  social-and-digital/     Images for posts, screens, and app promotion.',
    '  website-and-email/      Email signature and website embed snippet.',
    '  promo-copy/             Ready-to-paste launch copy.',
    '',
    'The three booklets are the print job:',
    '',
    '  *-pack.html       The day-one file. Posters, sticker sheets, clings,',
    '                    a table tent, pocket cards and counter strips.',
    '  *-posters.html    Every poster design, grouped by where it goes.',
    '  *-stickers.html   Every die-cut sheet, plus how to apply them.',
    '',
    'Open a booklet in any browser and print it, or save it as a PDF — it is laid',
    'out at A4 and every QR code in it stays vector at any size. Send',
    'printer-ready/ plus PRINT-JOB.txt to your printer.',
    '',
    'Your links',
    '-'.repeat(48),
    `  ${targetAction} ${name}:   ${targetUrl}`,
  ];
  if (phoneUrl) lines.push(`  Call:              ${phoneUrl.replace('tel:', '')}`);
  if (smartLinkUrl) lines.push(`  Get the app:       ${smartLinkUrl}`);
  if (presentationUrl) lines.push(`  Activation page:   ${presentationUrl}`);
  lines.push(
    '',
    '3-step quickstart',
    '-'.repeat(48),
    '  1. Confirm quantities and finishes with your printer using PRINT-JOB.txt.',
    '  2. Install the signature from website-and-email/ across customer-facing inboxes.',
    '  3. Share the launch copy and app poster with customers and your team.',
    '',
  );
  return lines.join('\n');
}

export function buildPrintJobText(pack: ActivationPackWithTarget): string {
  const name = ActivationPackService.getTargetName(pack);
  const stickerSize = pack.asset_config.sticker_size || '3x3';
  const lines = [
    `${name} — Starter Print Job`,
    '='.repeat(48),
    '',
  ];

  if (pack.touchpoints.qr_sticker) {
    lines.push(
      `QR stickers: ${ACTIVATION_PACK_STARTER_QUANTITIES.stickers}`,
      `  Size: ${stickerSize} inches`,
      '  Artwork: printer-ready/*sticker*.pdf',
      '',
    );
  }

  lines.push(
    `Customer-facing posters: ${ACTIVATION_PACK_STARTER_QUANTITIES.posters} total`,
    '  Size: A4 portrait',
    '  Artwork: printer-ready/*-pack.html and printer-ready/*-posters.html',
    '  Open in any browser and print, or save as PDF. Print at 100 percent —',
    '  scaling drops the smallest codes under the scannable floor.',
    '  Confirm the quantity split across poster designs before production.',
    '',
    'Stickers, labels, clings and magnets',
    '  Artwork: printer-ready/*-stickers.html',
    '  Each sheet carries its own die-cut size and stock note.',
    '',
    `App posters: ${ACTIVATION_PACK_STARTER_QUANTITIES.appPosters}`,
    '  Size: A4 portrait',
    '  Artwork: printer-ready/*app-poster*.pdf',
    '',
    'Before printing, confirm stock, finish, delivery address, deadline, bleed,',
    'and the final proof directly with the customer.',
    '',
  );
  return lines.join('\n');
}

const starterNoteFor = (type: GeneratedAssetType): string => {
  if (type === 'sticker_pdf') return `starter quantity ${ACTIVATION_PACK_STARTER_QUANTITIES.stickers}`;
  if (type === 'poster_pack_pdf') return `starter quantity ${ACTIVATION_PACK_STARTER_QUANTITIES.posters} total`;
  if (type === 'app_poster_pdf') return `starter quantity ${ACTIVATION_PACK_STARTER_QUANTITIES.appPosters}`;
  if (type === 'lookbook_pdf') return 'reference only — not production artwork';
  if (type === 'product_qr_png' || type === 'product_qr_svg') return 'opens the product manual page';
  return '';
};

/** Pure text manifest of the expected and included asset contract. */
export function buildManifestText(
  expected: GeneratedAssetType[],
  assets: GeneratedAsset[],
  generatedAt = new Date().toISOString(),
): string {
  const usableByType = new Map(
    assets.filter(hasUsableData).map((asset) => [asset.type, asset]),
  );
  const lines = [
    'Activation Pack Manifest',
    '='.repeat(48),
    `Generated: ${generatedAt}`,
    '',
    'Expected assets',
    '-'.repeat(48),
  ];

  for (const type of expected) {
    const asset = usableByType.get(type);
    const note = starterNoteFor(type);
    lines.push(
      `[${asset ? 'INCLUDED' : 'MISSING'}] ${ASSET_TYPE_INFO[type].label}` +
        `${asset ? ` — ${asset.filename}` : ''}` +
        `${note ? ` (${note})` : ''}`,
    );
  }

  const expectedSet = new Set(expected);
  const additional = assets.filter(
    (asset) => hasUsableData(asset) && !expectedSet.has(asset.type),
  );
  if (additional.length > 0) {
    lines.push('', 'Additional included assets', '-'.repeat(48));
    for (const asset of additional) {
      lines.push(`[INCLUDED] ${asset.label} — ${asset.filename}`);
    }
  }

  lines.push('');
  return lines.join('\n');
}

/** Build promo-copy/*.txt files from the pack's cached promo content. */
function buildPromoFiles(pack: ActivationPackWithTarget): { name: string; content: string }[] {
  const files: { name: string; content: string }[] = [];
  const promo = pack.promo_content || {};

  for (const option of PROMO_TEMPLATE_OPTIONS) {
    const tone = DEFAULT_PROMO_TONES[option.value];
    const template = promo[option.value]?.[tone];
    if (!template?.body) continue;
    const body = template.subject
      ? `Subject: ${template.subject}\n\n${template.body}`
      : template.body;
    files.push({ name: `${option.value}.txt`, content: body });
  }
  return files;
}

/**
 * Validate, bundle, and download the complete activation pack. No expected
 * asset is skipped: a missing or unreadable file rejects the whole operation.
 */
export async function downloadAssetsAsZip(
  assets: GeneratedAsset[],
  zipFilename: string,
  options?: ZipBundleOptions,
): Promise<void> {
  const expected = options?.expectedAssetTypes ||
    (options?.pack
      ? getExpectedActivationAssetTypes(options.pack.touchpoints)
      : assets.map((asset) => asset.type));
  assertCompleteActivationAssets(assets, expected);
  if (options?.pack) {
    assertCompleteDefaultPromoTemplates(options.pack);
  }

  // Resolve every blob before constructing the archive so an unreadable remote
  // asset cannot produce a partial pack that looks successful.
  const resolved = await Promise.all(
    assets.map(async (asset) => ({ asset, blob: await assetToBlob(asset) })),
  );

  const zip = new JSZip();
  const root = zip.folder(sanitizeFolder(zipFilename.replace(/\.zip$/i, '')))!;
  const qr = root.folder('qr-codes')!;
  const print = root.folder('printer-ready')!;
  const social = root.folder('social-and-digital')!;
  const website = root.folder('website-and-email')!;

  const folderFor = (type: GeneratedAssetType) => {
    if (type === 'qr_png' || type === 'qr_svg' || type === 'product_qr_png' || type === 'product_qr_svg') return qr;
    if (type === 'sticker_pdf' || type === 'app_poster_pdf' || type === 'poster_pack_pdf') return print;
    // Only reachable when a caller passes an explicit manifest that still asks
    // for the lookbook; it is not part of the default bundle any more.
    if (type === 'lookbook_pdf') return root.folder('placement-inspiration')!;
    if (type === 'social_card_png' || type === 'app_poster_png') return social;
    return website;
  };

  for (const { asset, blob } of resolved) {
    folderFor(asset.type).file(asset.filename, blob);
  }

  if (options?.pack) {
    // The three booklets, the same three the page offers. Built here rather
    // than passed in so the bundle and the buttons can never disagree about
    // what a customer's print material is.
    for (const booklet of await buildPackBooklets(options.pack)) {
      print.file(booklet.filename, booklet.html);
    }

    root.file('START-HERE.txt', buildReadmeText(options));
    root.file('PRINT-JOB.txt', buildPrintJobText(options.pack));
    root.file('MANIFEST.txt', buildManifestText(expected, assets));
    const promoFolder = root.folder('promo-copy')!;
    for (const file of buildPromoFiles(options.pack)) {
      promoFolder.file(file.name, file.content);
    }
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
